import { ComponentType, ReactNode } from 'react';

/**
 * Types
 */
import { InputSize } from '@astrumu/ui-library';
import { ModalName } from '../constants/modals';
import {
  FileType,
  JobRole,
  UserSkillsQuery,
  JobRecommendationQuery,
  LearningRecommendationFilters as LearningRecommendationFiltersType,
  SearchLearningRecommendationsInput
} from '@/graphql/generated';

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export interface CommonInputProps {
  cy: string;
  type: string;
  label: string;
  error: string;
  size: InputSize;
  disabled: boolean;
  readOnly: boolean;
  placeholder: string;
  isOptional: boolean;
  withBorder: boolean;
  note: ReactNode | string;
}

export type ChecklistItemId =
  | 'jst'
  | 'srb'
  | 'resume'
  | 'linkedin'
  | 'personal-information'
  | 'transition-preferences';

export type ProfileDocumentType = Extract<
  ChecklistItemId,
  'jst' | 'srb' | 'resume'
>;

export type ActionItemsModals = {
  [key in Extract<ChecklistItemId, ProfileDocumentType>]: {
    [key in 'update' | 'delete']: ModalName;
  };
} & {
  [key in Exclude<ChecklistItemId, ProfileDocumentType>]: {
    [key in 'update']: ModalName;
  };
};

export type ActionItemComponentProps = {
  isEditable?: boolean;
  onCompleted?: () => void;
  children: (actions: {
    title: string;
    isCompleted?: boolean;
    isLoading?: boolean;
    isSubmitting?: boolean;
    content: ReactNode;
    handleSubmit: () => void;
  }) => ReactNode;
};

export interface ChecklistItem {
  id: ChecklistItemId;
  title: string;
  reason: string;
  isRequired: boolean;
  isCompleted: boolean;
  explanation?: ReactNode;
  component: ComponentType<ActionItemComponentProps>;
}

export enum Match {
  Excellent = 'excellent',
  VeryGood = 'veryGood',
  Good = 'good',
  Moderate = 'moderate',
  Fair = 'fair',
  NotEnoughData = 'notEnoughData'
}

export enum JobRecommendationOverviewTabs {
  Overview = 'overview',
  SkillsGap = 'skillsGap'
}

export enum ProfileTabs {
  History = 'history',
  Skills = 'skills',
  Documents = 'documents',
  Saved = 'saved'
}

export type JobRecommendationJobRole =
  JobRecommendationQuery['jobRecommendation']['jobRole'];

export type ActionItemType = 'file' | 'linkedin' | 'account';

export interface DataValueEntity {
  id: string;
  name: string;
}

export interface CourseWeightedEntity extends DataValueEntity {
  source?: FileType;
  courses?: UserSkillsQuery['me']['hardSkills'][number]['courses'];
}

export interface ReplacedJobRole {
  from: Pick<JobRole, 'id' | 'name'>;
  to: Pick<JobRole, 'id' | 'name'>;
}

export type WeightedEntityName = 'hardSkill' | 'activity';

// Filters
export type FilterOption = { id: string; name: string };

export type RangeFilterOption = {
  id: string;
  name?: string;
  from?: number | null;
  to?: number | null;
};

interface CommonFilterGroup {
  name: string;
  title: string;
  queryName: string;
  extension?: ReactNode;
  onFilterClick: (id: string, name: string) => void;
}

export interface RegularFilterGroup extends CommonFilterGroup {
  type: 'single' | 'multiple';
  options: { [key in 'selected' | 'suggested']: FilterOption[] };
}
export interface RangeFilterGroup extends CommonFilterGroup {
  type: 'range';
  namesMapping: { [key in string]: string };
  options: { [key in 'selected' | 'suggested']: RangeFilterOption[] };
}

export type IFilterGroup = RegularFilterGroup | RangeFilterGroup;

// Learning recommendations filters
export type LearningRecommendationFilter = Exclude<
  keyof LearningRecommendationFiltersType,
  'awardLevels'
>;

export type RangeFilters = Extract<
  LearningRecommendationFilter,
  'price' | 'duration'
>;
export type SingleFilters = Extract<LearningRecommendationFilter, 'jobRoles'>;
export type MultipleFilters = Exclude<
  LearningRecommendationFilter,
  'price' | 'duration' | 'jobRoles'
>;

export type LearningRecommendationFilters = {
  [key in RangeFilters]: {
    [key in 'selected' | 'suggested']: RangeFilterOption[];
  };
} & {
  [key in SingleFilters]: {
    [key in 'selected' | 'suggested']: FilterOption[];
  };
} & {
  [key in MultipleFilters]: {
    [key in 'selected' | 'suggested']: FilterOption[];
  };
};

export type LearningRecommendationQueryFilter = Exclude<
  keyof SearchLearningRecommendationsInput,
  'awardLevelIds'
>;

export type LearningRecommendationQueryBaseFilters = {
  [key in LearningRecommendationQueryFilter]: string[];
};

export type LearningRecommendationQueryAdditionalFilters = {
  name: string;
};

export type LearningRecommendationQueryFilters =
  LearningRecommendationQueryBaseFilters &
    LearningRecommendationQueryAdditionalFilters;

export type CredentialType =
  | 'Degree'
  | 'Certification'
  | 'Certificate'
  | 'License'
  | 'Apprenticeship'
  | 'Workforce Development';
